import {
  ArrowRight,
  Moon,
  Sun,
  Fingerprint,
  LayoutDashboard,
  Link2,
  LogOut,
  RefreshCw,
  Users,
  Wallet,
} from 'lucide-react';
import { useEffect, useRef, useState, lazy, Suspense, type FormEvent, type ReactNode } from 'react';
import { translatePartner, type PartnerMessageKey } from '@washed/i18n/partner';
type WashedLocale = 'fr' | 'en';
import { fixture, formatXof, previewAgreement, type Dashboard } from './model.js';
import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import { CodeInput } from './CodeInput.js';
import { CustomCode } from './CustomCode.js';
import { Boundary } from './Boundary.js';
import {
  AgreementCopy,
  AgreementSign,
  type AgreementData,
  type SignedAgreement,
} from './Agreement.js';

const DashboardOverview = lazy(() =>
  import('./DashboardOverview.js').then((m) => ({ default: m.DashboardOverview })),
);
const EarningsChart = lazy(() =>
  import('./DashboardOverview.js').then((m) => ({ default: m.EarningsChart })),
);

type Tab = 'overview' | 'referrals' | 'earnings' | 'share';
const tabs = [
  ['overview', LayoutDashboard],
  ['referrals', Users],
  ['earnings', Wallet],
  ['share', Link2],
] as const;
const statusKeys = new Set([
  'ATTRIBUTED',
  'HELD_FIRST_SERVICE_ISSUE',
  'QUALIFIED_PENDING_REVIEW',
  'REWARDED',
  'REJECTED',
  'PENDING_RETENTION',
  'PAYABLE',
  'HELD',
  'CANCELLED',
  'PAID',
]);
/** `name` is set when the code came from the sign-up link (/rejoindre), so « resend » re-applies. */
type SignIn = { step: 'email' } | { step: 'code'; email: string; name?: string };
type Problem = 'error' | 'codeInvalid' | 'rateLimited' | 'passkeyFailed' | 'joinPaused';
async function api<T>(path: string, body?: object): Promise<T> {
  const res = await fetch(`/v1/partner/${path}`, {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      // The language the creator reads the portal in: her e-mails come in it too.
      'X-Washed-Locale': localStorage.getItem('partner.locale') === 'en' ? 'en' : 'fr',
      ...(body ? { 'X-Partner-CSRF': sessionStorage.getItem('partner.csrf') ?? '' } : {}),
    },
    ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    // The status is the message (status() below); the API's code rides beside it.
    const code = await res
      .json()
      .then((b: { code?: string }) => b.code)
      .catch(() => undefined);
    throw Object.assign(new Error(String(res.status)), { code });
  }
  return res.json() as Promise<T>;
}
const status = (e: unknown) => Number((e as Error).message);
export function App() {
  const [locale, setLocale] = useState<WashedLocale>(() =>
    localStorage.getItem('partner.locale') === 'en' ? 'en' : 'fr',
  );
  const t = (key: string) => translatePartner(`partner.${key}` as PartnerMessageKey, locale);
  const [policy, setPolicy] = useState('');
  const [creatorAccess, setCreatorAccess] = useState(true);
  // Each creator reads the terms she accepted: v2 pays by plan, v1 (accepted before
  // 2026-10-25) a flat 2 000.
  const terms = () => {
    const accepted = data?.profile.policy_version ?? policy;
    return t(
      accepted === 'creator-first-payment-v2'
        ? 'termsCreatorBody'
        : accepted === 'creator-first-payment-v1'
          ? 'termsCreatorLegacyBody'
          : 'termsBody',
    );
  };
  const [tab, setTab] = useState<Tab>('overview'),
    [data, setData] = useState<Dashboard | null>(null),
    [error, setError] = useState(false),
    [busy, setBusy] = useState(false),
    [logged, setLogged] = useState<boolean | null>(null),
    [accepted, setAccepted] = useState(false),
    [copied, setCopied] = useState<'code' | 'link' | null>(null),
    [customDates, setCustomDates] = useState(false),
    // A creator signs the accord before anything else (2026-11-02); the signed copy shows
    // under « Mon lien ».
    [agreementRequired, setAgreementRequired] = useState(false),
    [signedAgreement, setSignedAgreement] = useState<SignedAgreement | null>(null),
    [justSigned, setJustSigned] = useState(false),
    [partnerStatus, setPartnerStatus] = useState<string | null>(null),
    [meName, setMeName] = useState(''),
    // True once a code was typed on this page: that session is the person signing up now.
    [signedInHere, setSignedInHere] = useState(false);
  const [signIn, setSignIn] = useState<SignIn>({ step: 'email' }),
    [code, setCode] = useState(''),
    [problem, setProblem] = useState<Problem | null>(null),
    [passkeys, setPasskeys] = useState<{ id: string }[] | null>(null),
    [passkeyReady, setPasskeyReady] = useState(false),
    [passkeyNotice, setPasskeyNotice] = useState<'passkeyEnabled' | 'passkeyAddFailed' | null>(
      null,
    ),
    [passkeyLater, setPasskeyLater] = useState(
      () => localStorage.getItem('partner.passkey.later') === '1',
    ),
    [resent, setResent] = useState(false);
  const codeInput = useRef<HTMLInputElement>(null);
  /** Bumped at sign-out so a request from the session that ended cannot fill this one. */
  const sessionRef = useRef(0);
  const codeForm = useRef<HTMLFormElement>(null);
  const [from, setFrom] = useState(new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10)),
    [to, setTo] = useState(new Date().toISOString().slice(0, 10)),
    [page, setPage] = useState(1);
  const isPreview = import.meta.env.DEV && new URLSearchParams(location.search).has('preview');
  const [mode, setMode] = useState(new URLSearchParams(location.search).get('data') ?? 'normal');
  const landing = location.pathname.startsWith('/invite/');
  // The sign-up link (2026-11-02): the creator applies herself, the office approves.
  const joining = location.pathname.startsWith('/rejoindre');
  const publicCode = (location.pathname.split('/')[2] ?? '').toUpperCase();
  const whatsAppNumber = String(import.meta.env['VITE_WASHED_WHATSAPP_NUMBER'] ?? '').replace(
    /\D/gu,
    '',
  );
  const validWhatsAppNumber = /^228\d{8}$/u.test(whatsAppNumber);
  const publicOrigin = import.meta.env['VITE_PUBLIC_WEB_ORIGIN'] ?? 'https://washedafrica.com';
  // Without a code there is no link: « …/invite/ » would copy a page that names nobody.
  const shareLink = data?.profile.code
    ? `${import.meta.env['VITE_PARTNER_PUBLIC_ORIGIN'] ?? location.origin}/invite/${data.profile.code}`
    : '';
  const date = (v: string) =>
    new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'Africa/Lome' }).format(
      new Date(v),
    );
  const money = (v: string) => formatXof(v, locale);
  async function load(nextPage = page, fromDate = from, toDate = to) {
    setBusy(true);
    setError(false);
    try {
      const result = await api<Dashboard>(
        `dashboard?from=${fromDate}&to=${toDate}&page=${nextPage}`,
      );
      setData(result);
      setPage(nextPage);
    } catch (e) {
      if (status(e) === 401) setLogged(false);
      else setError(true);
    } finally {
      setBusy(false);
    }
  }
  async function start() {
    try {
      const me = await api<{
        acceptedAt: string | null;
        csrf: string;
        policyVersion: string;
        kind: string;
        agreementRequired?: boolean;
        status?: string;
        displayName?: string;
      }>('me');
      sessionStorage.setItem('partner.csrf', me.csrf);
      setLogged(true);
      setPolicy(me.policyVersion);
      setCreatorAccess(me.kind === 'CREATOR');
      setAccepted(!!me.acceptedAt);
      setAgreementRequired(!!me.agreementRequired);
      setPartnerStatus(me.status ?? null);
      setMeName(me.displayName ?? '');
      // Waiting for the office: her signed copy is all there is to show.
      if (me.status === 'APPLIED' && !me.agreementRequired) {
        const session = sessionRef.current;
        void api<AgreementData>('agreement')
          .then((a) => {
            if (sessionRef.current === session) setSignedAgreement(a.signed);
          })
          .catch(() => undefined);
      }
      if (me.acceptedAt && me.kind === 'CREATOR' && !me.agreementRequired) {
        void load();
        void loadPasskeys();
        // A copy that arrives after sign-out (or for another creator's session) is dropped: it
        // carries the signer's name, email, accounts and Mobile Money number.
        const session = sessionRef.current;
        void api<AgreementData>('agreement')
          .then((a) => {
            if (sessionRef.current === session) setSignedAgreement(a.signed);
          })
          .catch(() => undefined);
      }
    } catch {
      setLogged(false);
    }
  }
  const [theme, setTheme] = useState<'dark' | 'light'>(() =>
    localStorage.getItem('partner.theme') === 'light' ? 'light' : 'dark',
  );
  useEffect(() => {
    document.documentElement.dataset['theme'] = theme;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', theme === 'dark' ? '#000000' : '#ffffff');
    localStorage.setItem('partner.theme', theme);
  }, [theme]);
  useEffect(() => {
    document.documentElement.lang = locale;
    localStorage.setItem('partner.locale', locale);
  }, [locale]);
  useEffect(() => {
    if (landing) return;
    if (isPreview) {
      setData(fixture(mode));
      setLogged(true);
      setAccepted(true);
      setAgreementRequired(new URLSearchParams(location.search).has('agreement'));
      return;
    }
    void start();
  }, [mode]);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(null), 2500);
    return () => clearTimeout(timer);
  }, [copied]);
  async function action(run: () => Promise<void>) {
    setBusy(true);
    setError(false);
    try {
      await run();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }
  async function copy(what: 'code' | 'link', value: string) {
    await action(async () => {
      await navigator.clipboard.writeText(value);
      setCopied(what);
    });
  }
  async function requestCode(email: string, again = false, name?: string) {
    setBusy(true);
    setProblem(null);
    setResent(false);
    try {
      if (name) await api('apply', { name, email });
      else await api('auth/request', { email });
      setSignIn({ step: 'code', email, ...(name ? { name } : {}) });
      setCode('');
      setResent(again);
    } catch (e) {
      setProblem(
        (e as { code?: string }).code === 'core.partner.applications_paused'
          ? 'joinPaused'
          : status(e) === 429
            ? 'rateLimited'
            : 'error',
      );
    } finally {
      setBusy(false);
    }
  }
  async function submitEmail(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const name = joining ? String(form.get('name') ?? '').trim() : undefined;
    await requestCode(String(form.get('email')).trim(), false, name);
  }
  async function submitCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // The sixth digit sends the form by `requestSubmit()`, past the button's `disabled`: while
    // a code is being verified, retyping must not start a second verification.
    if (signIn.step !== 'code' || busy) return;
    setBusy(true);
    setProblem(null);
    setResent(false);
    try {
      const result = await api<{ csrf: string }>('auth/verify', { email: signIn.email, code });
      sessionStorage.setItem('partner.csrf', result.csrf);
      setSignedInHere(true);
      await start();
    } catch (err) {
      const s = status(err);
      setProblem(s === 401 || s === 400 ? 'codeInvalid' : s === 429 ? 'rateLimited' : 'error');
      setCode('');
      codeInput.current?.focus();
    } finally {
      setBusy(false);
    }
  }
  const feedback = error && (
    <div className="notice problem" role="alert">
      {t('error')}{' '}
      <button className="text-button" onClick={() => void load()}>
        {t('retry')}
      </button>
    </div>
  );
  const brand = (
    <a className="brand" href={publicOrigin} aria-label="WASHED" translate="no">
      washed<span>.</span>
    </a>
  );
  const language = (
    <button
      className="text-button"
      onClick={() => setLocale(locale === 'fr' ? 'en' : 'fr')}
      aria-label={locale === 'fr' ? 'Switch to English' : 'Passer en français'}
    >
      <span lang={locale === 'fr' ? 'en' : 'fr'}>{locale === 'fr' ? 'English' : 'Français'}</span>
    </button>
  );
  const themeSwitch = (
    <button
      className="text-button"
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      aria-label={t(theme === 'dark' ? 'themeLight' : 'themeDark')}
      title={t(theme === 'dark' ? 'themeLight' : 'themeDark')}
    >
      {theme === 'dark' ? (
        <Sun size={18} aria-hidden="true" />
      ) : (
        <Moon size={18} aria-hidden="true" />
      )}
    </button>
  );
  useEffect(() => {
    // Only offer passkeys where this phone or computer can check a fingerprint, face or PIN.
    void platformAuthenticatorIsAvailable()
      .then(setPasskeyReady)
      .catch(() => setPasskeyReady(false));
  }, []);
  async function loadPasskeys() {
    try {
      setPasskeys((await api<{ items: { id: string }[] }>('passkeys')).items);
    } catch {
      setPasskeys(null);
    }
  }
  /** Fingerprint / Face ID sign-in: the phone offers its passkey; no email to type. */
  async function signInWithPasskey() {
    setBusy(true);
    setProblem(null);
    setResent(false);
    try {
      const { challengeId, options } = await api<{ challengeId: string; options: never }>(
        'passkeys/sign-in/options',
        {},
      );
      const response = await startAuthentication({ optionsJSON: options });
      const result = await api<{ csrf: string }>('passkeys/sign-in', { challengeId, response });
      sessionStorage.setItem('partner.csrf', result.csrf);
      await start();
    } catch (e) {
      setProblem(status(e) === 429 ? 'rateLimited' : 'passkeyFailed');
    } finally {
      setBusy(false);
    }
  }
  async function enablePasskey() {
    setBusy(true);
    setPasskeyNotice(null);
    try {
      const { challengeId, options } = await api<{ challengeId: string; options: never }>(
        'passkeys/register/options',
        {},
      );
      const response = await startRegistration({ optionsJSON: options });
      await api('passkeys/register', { challengeId, response });
      await loadPasskeys();
      setPasskeyNotice('passkeyEnabled');
    } catch {
      setPasskeyNotice('passkeyAddFailed');
    } finally {
      setBusy(false);
    }
  }
  async function disablePasskeys() {
    setBusy(true);
    try {
      for (const key of passkeys ?? []) await api('passkeys/remove', { id: key.id });
      await loadPasskeys();
      setPasskeyNotice(null);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }
  /** Signing out always ends on the sign-in page, even when the session had already ended. */
  async function signOut() {
    setBusy(true);
    try {
      if (!isPreview) await api('auth/logout', {});
    } catch {
      // Expired or already revoked: nothing left to end on the server.
    } finally {
      sessionStorage.removeItem('partner.csrf');
      setSignIn({ step: 'email' });
      setCode('');
      setProblem(null);
      setResent(false);
      setError(false);
      setData(null);
      setPasskeys(null);
      setPasskeyNotice(null);
      setAccepted(false);
      setCreatorAccess(true);
      sessionRef.current += 1;
      setSignedAgreement(null);
      setJustSigned(false);
      setAgreementRequired(false);
      setPartnerStatus(null);
      setTab('overview');
      setLogged(false);
      setBusy(false);
    }
  }
  const logout = (
    <button className="text-button" disabled={busy} onClick={() => void signOut()}>
      <LogOut size={18} aria-hidden="true" />
      <span className="logout-label">{t('logout')}</span>
    </button>
  );
  const single = (body: ReactNode, signedIn = false) => (
    <div className="single">
      <header className="single-header">
        {brand}
        <div className="single-actions">
          {themeSwitch}
          {language}
          {signedIn && logout}
        </div>
      </header>
      <main className="single-body">{body}</main>
    </div>
  );
  if (landing)
    return single(
      <>
        <h1>{t('landing')}</h1>
        <p className="lead">{t('landingBody')}</p>
        {/^(?:[A-Z0-9]{8}|INF-[A-Z0-9]{2,24})$/u.test(publicCode) ? (
          <>
            <p className="code" translate="no">
              {publicCode}
            </p>
            {validWhatsAppNumber ? (
              <a
                className="primary wide"
                href={`https://wa.me/${whatsAppNumber}?text=${encodeURIComponent(`Bonjour WASHED. Code de parrainage : ${publicCode}`)}`}
              >
                {t('join')}
              </a>
            ) : (
              <p className="notice">{t('contactPending')}</p>
            )}
          </>
        ) : (
          <p className="notice problem" role="alert">
            {t('error')}
          </p>
        )}
      </>,
    );
  if (!logged) {
    const notice = problem ? (
      <p className="notice problem" role="alert">
        {t(problem)}
      </p>
    ) : resent ? (
      <p className="notice" role="status">
        {t('resent')}
      </p>
    ) : null;
    if (logged === null) return single(<p role="status">{t('loading')}</p>);
    if (signIn.step === 'email')
      return single(
        <>
          <h1>{t(joining ? 'joinTitle' : 'loginTitle')}</h1>
          <p className="lead">{t(joining ? 'joinIntro' : 'loginIntro')}</p>
          {notice}
          <form className="stack" onSubmit={(e) => void submitEmail(e)}>
            {joining && (
              <>
                <label htmlFor="name">{t('joinName')}</label>
                <input id="name" name="name" autoComplete="name" maxLength={120} required />
              </>
            )}
            <label htmlFor="email">{t('email')}</label>
            <input
              id="email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={254}
              required
            />
            <button className="primary wide" disabled={busy}>
              {t('sendCode')}
            </button>
          </form>
          {!joining && browserSupportsWebAuthn() && (
            <button
              className="secondary wide"
              disabled={busy}
              onClick={() => void signInWithPasskey()}
            >
              <Fingerprint size={20} aria-hidden="true" />
              {t('passkeySignIn')}
            </button>
          )}
          <a className="text-button join-link" href={joining ? '/' : '/rejoindre'}>
            {t(joining ? 'joinHaveAccount' : 'joinCta')}
          </a>
        </>,
      );
    return single(
      <>
        <h1>{t('codeTitle')}</h1>
        <p className="lead">{t(signIn.name ? 'joinCodeIntro' : 'codeIntro')}</p>
        <p className="sent-to">
          <strong translate="no">{signIn.email}</strong>
          <button
            className="text-button"
            disabled={busy}
            onClick={() => {
              setSignIn({ step: 'email' });
              setProblem(null);
              setResent(false);
            }}
          >
            {t('changeEmail')}
          </button>
        </p>
        {notice}
        <form ref={codeForm} className="stack" onSubmit={(e) => void submitCode(e)}>
          <label htmlFor="code">{t('codeLabel')}</label>
          <CodeInput
            ref={codeInput}
            id="code"
            name="code"
            value={code}
            onChange={(next) => {
              setCode(next);
              if (problem === 'codeInvalid') setProblem(null);
            }}
            onComplete={() => codeForm.current?.requestSubmit()}
            invalid={problem === 'codeInvalid'}
            autoFocus
          />
          <button className="primary wide" disabled={busy || code.length !== 6}>
            {t('confirm')}
          </button>
        </form>
        <button
          className="text-button"
          disabled={busy}
          onClick={() => void requestCode(signIn.email, true, signIn.name)}
        >
          {t('resend')}
        </button>
      </>,
    );
  }
  // The sign-up link opened on a phone already signed in to another space (the founder's own
  // creator account, 2026-11-03): say whose it is instead of asking that account to sign.
  if (joining && !signedInHere && partnerStatus !== 'APPLIED' && partnerStatus !== 'REFUSED')
    return single(
      <>
        <h1>{t('joinSignedInTitle')}</h1>
        <p className="lead">
          {t('joinSignedInAs')} <strong>{meName}</strong>. {t('joinSignedInBody')}
        </p>
        <a className="primary wide" href="/">
          {t('joinOpenMine')}
        </a>
        <button className="secondary wide" disabled={busy} onClick={() => void signOut()}>
          {t('joinSomeoneElse')}
        </button>
      </>,
      true,
    );
  if (partnerStatus === 'REFUSED')
    return single(
      <>
        <h1>{t('refusedTitle')}</h1>
        <p className="lead">{t('refusedBody')}</p>
      </>,
      true,
    );
  if (creatorAccess && agreementRequired)
    return single(
      <AgreementSign
        t={t}
        request={isPreview ? previewAgreement : api}
        onSigned={(signed) => {
          setSignedAgreement(signed);
          setJustSigned(true);
          setAgreementRequired(false);
          window.scrollTo(0, 0);
          // An applicant waits for the office: no code, no dashboard yet.
          if (partnerStatus === 'APPLIED') return;
          setAccepted(true);
          // The signing page is long: start the dashboard at its top, where the notice is.
          window.scrollTo(0, 0);
          if (isPreview) return setData(fixture(mode));
          void load();
          void loadPasskeys();
        }}
      />,
      true,
    );
  if (partnerStatus === 'APPLIED')
    return single(
      <>
        <h1>{t('waitingTitle')}</h1>
        <p className="lead">{t('waitingBody')}</p>
        {justSigned && (
          <div className="notice agreement-signed-notice" role="status">
            {t('agreementSigned')}{' '}
            <a className="text-button" href="/v1/partner/agreement/pdf" download>
              {t('agreementDownload')}
            </a>
          </div>
        )}
        {signedAgreement && <AgreementCopy t={t} signed={signedAgreement} date={date} />}
      </>,
      true,
    );
  if (!accepted)
    return single(
      <>
        <h1>{t('termsTitle')}</h1>
        <p className="lead">{terms()}</p>
        <p className="caption">
          {t('policy')} · {policy}
        </p>
        {feedback}
        <button
          disabled={busy}
          className="primary wide"
          onClick={() =>
            void action(async () => {
              await api('terms/accept', {});
              setAccepted(true);
              if (creatorAccess) {
                await load();
                await loadPasskeys();
              }
            })
          }
        >
          {t('accept')}
        </button>
      </>,
      true,
    );
  if (!creatorAccess)
    return single(
      <>
        <h1>{t('portal')}</h1>
        <p className="lead">{t('creatorAccessOnly')}</p>
      </>,
      true,
    );
  const statusLabel = (row: Dashboard['referrals'][number]) => {
    const key = row.bounty_status ?? row.status;
    return t(statusKeys.has(key) ? key : 'held');
  };
  const activity = (
    <section className="section">
      <div className="section-heading">
        <div>
          <h2>{t('recent')}</h2>
          <p>{t('activityIntro')}</p>
        </div>
        <span className="count">{data?.summary.attributed ?? 0}</span>
      </div>
      {data?.referrals.length ? (
        <>
          <div className="table-scroll">
            <table className="referrals-table">
              <thead>
                <tr>
                  <th>{t('reference')}</th>
                  <th>{t('date')}</th>
                  <th>{t('status')}</th>
                  <th className="numeric">{t('amount')}</th>
                </tr>
              </thead>
              <tbody>
                {data.referrals.slice(0, tab === 'overview' ? 5 : 25).map((row) => (
                  <tr key={row.reference}>
                    <td className="reference">{row.reference}</td>
                    <td>{date(row.attributed_at)}</td>
                    <td>
                      <span
                        className={`status${row.bounty_status === 'PAID' || row.bounty_status === 'PAYABLE' ? ' positive' : ''}`}
                      >
                        {statusLabel(row)}
                      </span>
                    </td>
                    <td className="numeric">{row.amount ? money(row.amount) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {tab === 'referrals' && (
            <div className="pagination">
              <button
                className="secondary"
                disabled={busy || page === 1}
                onClick={() => void load(page - 1)}
              >
                {t('previous')}
              </button>
              <span>
                {t('page')} {page}
              </span>
              <button
                className="secondary"
                disabled={busy || page * 25 >= (data.referrals[0]?.total ?? 0)}
                onClick={() => void load(page + 1)}
              >
                {t('next')}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="group empty">
          <h3>{t('noReferrals')}</h3>
          <p>{t('noReferralsBody')}</p>
          <button className="secondary" onClick={() => setTab('share')}>
            {t('share')}
          </button>
        </div>
      )}
    </section>
  );
  const name = data?.profile.display_name ?? '';
  const heading = {
    overview: name ? (
      <>
        {t('hello')},{' '}
        <span className={`greeting-name${name.length > 20 ? ' long' : ''}`}>{name}</span>
      </>
    ) : (
      t('hello')
    ),
    referrals: t('referrals'),
    earnings: t('earnings'),
    share: t('shareTitle'),
  }[tab];
  const intro = {
    overview: 'dashboardIntro',
    referrals: 'intro',
    earnings: 'earningsIntro',
    share: 'shareIntro',
  }[tab];
  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          {brand}
          <span className="sidebar-portal">{t('portal')}</span>
        </div>
        <nav aria-label={t('portal')}>
          {tabs.map(([item, Icon]) => (
            <button
              key={item}
              aria-current={tab === item ? 'page' : undefined}
              onClick={() => setTab(item)}
            >
              <Icon size={20} strokeWidth={1.75} aria-hidden="true" />
              {t(item)}
            </button>
          ))}
        </nav>
        <div className="sidebar-identity">
          <span className="avatar" aria-hidden="true">
            {name.slice(0, 1) || 'W'}
          </span>
          <span>
            <strong>{name}</strong>
            <span>{t('creator')}</span>
          </span>
        </div>
        <div className="sidebar-actions">
          {themeSwitch}
          {language}
          {logout}
        </div>
      </aside>
      <main className="content" data-tab={tab} aria-busy={busy}>
        <div className="page-heading">
          <div>
            <h1>{heading}</h1>
            <p className="lead">{t(intro)}</p>
          </div>
          {tab !== 'share' && (
            <button
              className="secondary refresh"
              disabled={busy || isPreview}
              onClick={() => void load()}
            >
              <RefreshCw size={18} aria-hidden="true" />
              <span>{t('refresh')}</span>
            </button>
          )}
        </div>
        {feedback}
        {justSigned && (
          <div className="notice agreement-signed-notice" role="status">
            {t('agreementSigned')}{' '}
            <a className="text-button" href="/v1/partner/agreement/pdf" download>
              {t('agreementDownload')}
            </a>
          </div>
        )}
        {/* A tab whose chunk is gone after a deploy, or that throws, says so here with
            « Recharger » instead of blanking the page (audit C3). */}
        <Boundary t={t} resetKey={tab}>
          {!data ? (
            <p role="status">{t('loading')}</p>
          ) : (
            <>
              {(tab === 'overview' || tab === 'referrals' || tab === 'earnings') && (
                <form
                  className="filters"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!isPreview) void load(1);
                  }}
                >
                  <div className="segmented" role="group" aria-label={t('period')}>
                    {[30, 90].map((days) => (
                      <button
                        type="button"
                        aria-pressed={
                          Math.round((Date.parse(to) - Date.parse(from)) / 86400000) === days - 1
                        }
                        key={days}
                        onClick={() => {
                          const end = new Date();
                          setTo(end.toISOString().slice(0, 10));
                          const start = new Date(end.getTime() - (days - 1) * 86400000)
                            .toISOString()
                            .slice(0, 10);
                          setFrom(start);
                          if (!isPreview) void load(1, start, end.toISOString().slice(0, 10));
                        }}
                      >
                        {t(days === 30 ? 'last30' : 'last90')}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="text-button custom-toggle"
                    aria-expanded={customDates}
                    onClick={() => setCustomDates(!customDates)}
                  >
                    {t('custom')}
                  </button>
                  <div className={`dates${customDates ? ' open' : ''}`}>
                    <label>
                      {t('from')}
                      <input
                        type="date"
                        value={from}
                        max={to}
                        onChange={(e) => setFrom(e.target.value)}
                        required
                      />
                    </label>
                    <label>
                      {t('to')}
                      <input
                        type="date"
                        value={to}
                        min={from}
                        onChange={(e) => setTo(e.target.value)}
                        required
                      />
                    </label>
                    <button className="secondary" disabled={busy || isPreview}>
                      {t('apply')}
                    </button>
                  </div>
                </form>
              )}
              {tab === 'overview' &&
                !isPreview &&
                passkeyReady &&
                passkeys?.length === 0 &&
                !passkeyLater && (
                  <section className="group passkey-offer" aria-labelledby="passkey-offer-title">
                    <h2 id="passkey-offer-title">{t('passkeyOfferTitle')}</h2>
                    <p>{t('passkeyOfferBody')}</p>
                    <div className="passkey-actions">
                      <button
                        className="secondary"
                        disabled={busy}
                        onClick={() => void enablePasskey()}
                      >
                        <Fingerprint size={20} aria-hidden="true" />
                        {t('passkeyEnable')}
                      </button>
                      <button
                        className="text-button"
                        onClick={() => {
                          localStorage.setItem('partner.passkey.later', '1');
                          setPasskeyLater(true);
                        }}
                      >
                        {t('passkeyLater')}
                      </button>
                    </div>
                  </section>
                )}
              {passkeyNotice && (
                <p
                  className={`notice${passkeyNotice === 'passkeyAddFailed' ? ' problem' : ''}`}
                  role="status"
                >
                  {t(passkeyNotice)}
                </p>
              )}
              {tab === 'overview' && (
                <>
                  <Suspense
                    fallback={
                      <div className="dashboard-loading" role="status" aria-label={t('loading')}>
                        <div className="metrics">
                          {[0, 1, 2, 3].map((i) => (
                            <div key={i} className="group loading-metric" />
                          ))}
                        </div>
                        <div className="dashboard-grid">
                          {[0, 1, 2, 3].map((i) => (
                            <div key={i} className="group loading-panel" />
                          ))}
                        </div>
                      </div>
                    }
                  >
                    <DashboardOverview
                      data={data}
                      locale={locale}
                      t={t}
                      shareLink={shareLink}
                      copied={copied === 'link'}
                      onCopy={() => void copy('link', shareLink)}
                      onShare={() => setTab('share')}
                      onEarnings={() => setTab('earnings')}
                    />
                  </Suspense>
                  {activity}
                  {data.referrals.length > 0 && (
                    <button className="text-button view-all" onClick={() => setTab('referrals')}>
                      {t('allActivity')}
                      <ArrowRight size={18} aria-hidden="true" />
                    </button>
                  )}
                </>
              )}
              {tab === 'referrals' && (
                <>
                  <div className="metrics two">
                    {(['qualified', 'active'] as const).map((key) => (
                      <div key={key} className="group metric">
                        <span className="metric-label">{t(key)}</span>
                        <strong className="figure">{data.summary[key]}</strong>
                        <p>{t(`${key}Help`)}</p>
                      </div>
                    ))}
                  </div>
                  {activity}
                </>
              )}
              {tab === 'earnings' && (
                <>
                  <div className="metrics">
                    {(['payable', 'held', 'earned', 'paid'] as const).map((key) => (
                      <div key={key} className="group metric">
                        <span className="metric-label">{t(key === 'paid' ? 'paidOut' : key)}</span>
                        <strong className="figure">
                          {money(
                            key === 'payable'
                              ? (
                                  BigInt(data.earnings.earned) - BigInt(data.earnings.paid)
                                ).toString()
                              : data.earnings[key],
                          )}
                        </strong>
                      </div>
                    ))}
                  </div>
                  <p className="caption note">{t('balanceNote')}</p>
                  <Suspense fallback={null}>
                    <EarningsChart data={data} locale={locale} t={t} />
                  </Suspense>
                  <section className="section">
                    <h2>{t('payouts')}</h2>
                    {data.payouts.length ? (
                      <div className="table-scroll">
                        <table className="payouts-table">
                          <thead>
                            <tr>
                              <th>{t('paidOn')}</th>
                              <th>{t('provider')}</th>
                              <th>{t('reference')}</th>
                              <th className="numeric">{t('amount')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.payouts.map((p) => (
                              <tr key={p.payout_id}>
                                <td className="paid-on">{date(p.paid_at)}</td>
                                <td className="provider">{p.provider}</td>
                                <td className="reference">{p.reference}</td>
                                <td className="numeric">{money(p.amount_xof)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="group empty">{t('noPayouts')}</p>
                    )}
                  </section>
                  {signedAgreement && <AgreementCopy t={t} signed={signedAgreement} date={date} />}
                  <section className="section terms">
                    <h2>{t('terms')}</h2>
                    <p>{terms()}</p>
                    <p className="caption">
                      {t('policy')} · {data.profile.policy_version}
                    </p>
                  </section>
                </>
              )}
              {tab === 'share' && (
                <>
                  <section className="group share-panel">
                    <span className="metric-label">{t('code')}</span>
                    <p className="code" translate="no">
                      {data.profile.code ?? '—'}
                    </p>
                    <button
                      className="secondary"
                      disabled={!data.profile.code}
                      onClick={() => void copy('code', data.profile.code!)}
                    >
                      {t(copied === 'code' ? 'copied' : 'copyCode')}
                    </button>
                    <p className="share-url">
                      {shareLink ? (
                        <>
                          {shareLink.slice(0, shareLink.indexOf('/invite/'))}
                          <span className="link-path">
                            {shareLink.slice(shareLink.indexOf('/invite/'))}
                          </span>
                        </>
                      ) : (
                        '—'
                      )}
                    </p>
                    <button
                      className="primary"
                      disabled={!shareLink}
                      onClick={() => void copy('link', shareLink)}
                    >
                      {t(copied === 'link' ? 'copied' : 'copy')}
                    </button>
                    <span className="sr-only" role="status">
                      {copied ? t('copied') : ''}
                    </span>
                  </section>
                  <CustomCode
                    codes={data.codes}
                    t={t}
                    date={date}
                    preview={isPreview}
                    onStale={() => void load()}
                    onChanged={(next) =>
                      setData({
                        ...data,
                        codes: next,
                        profile: { ...data.profile, code: next.code },
                      })
                    }
                  />
                  {signedAgreement && <AgreementCopy t={t} signed={signedAgreement} date={date} />}
                  <section className="section terms">
                    <h2>{t('terms')}</h2>
                    <p>{terms()}</p>
                    <p className="caption">
                      {t('policy')} · {data.profile.policy_version}
                    </p>
                  </section>
                </>
              )}
              {!!passkeys?.length && (
                <p className="caption passkey-status">
                  {t('passkeyEnabled')}
                  <button
                    className="text-button"
                    disabled={busy}
                    onClick={() => void disablePasskeys()}
                  >
                    {t('passkeyRemove')}
                  </button>
                </p>
              )}
              {/* Phones keep the top bar to the logo and tabs; these move here. */}
              <div className="mobile-actions">
                {themeSwitch}
                {language}
                {logout}
              </div>
              <footer className="caption">
                {t('updated')}{' '}
                {new Intl.DateTimeFormat(locale, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                  timeZone: 'Africa/Lome',
                }).format(new Date(data.updatedAt))}{' '}
                · Lomé
              </footer>
            </>
          )}
        </Boundary>
      </main>
      {isPreview && (
        <div className="preview-tools">
          <span>{t('demo')}</span>
          {['normal', 'worst', 'empty'].map((v) => (
            <button
              key={v}
              aria-pressed={mode === v}
              onClick={() => {
                setMode(v);
                history.replaceState(null, '', `?preview=1&data=${v}`);
              }}
            >
              {t(v === 'normal' ? 'demoNormal' : v === 'worst' ? 'demoWorst' : 'demoEmpty')}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
